import { Box, Typography, Grid, Paper } from '@mui/material';
import { Circle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AgentStatus } from '@bandido/shared/constants';
import { useTwilio } from '@frontend/features/communications/TwilioContextProvider/provider';

const getStatusColor = (status: string) => {
  switch (status) {
    case AgentStatus.AVAILABLE:
      return 'success.main';
    case AgentStatus.IN_CALL:
      return 'info.main';
    case AgentStatus.WORKING_IN_CASE:
      return 'primary.main';
    case AgentStatus.AFTER_CALL:
      return 'warning.main';
    case AgentStatus.BREAK:
      return 'warning.main';
    case AgentStatus.LUNCH:
      return 'warning.main';
    case AgentStatus.NOT_AVAILABLE:
      return 'error.main';
    case AgentStatus.DISCONNECTED:
      return 'error.main';
    default:
      return 'text.secondary';
  }
};

export const AgentsStatusSection = () => {
  const { t } = useTranslation();

  const { connectedAgents } = useTwilio();

  const statusCounts = connectedAgents.reduce(
    (acc, agent) => {
      acc[agent.status as AgentStatus] = (acc[agent.status as AgentStatus] || 0) + 1;
      return acc;
    },
    {} as Record<AgentStatus, number>,
  );

  const agentsList = connectedAgents.map((agent) => ({
    ...agent,
    status: agent.status as AgentStatus,
    lastStatusDate: new Date(agent.since),
  }));
  const total = connectedAgents.length;

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        {t('dashboard.sections.agentsStatus')}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard.agentsStatus.agentStates')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Object.values(AgentStatus).map((status) => {
                const count = statusCounts[status] || 0;
                return (
                  <Box
                    key={status}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1,
                      px: 1.5,
                      borderRadius: 1,
                      backgroundColor: count > 0 ? 'grey.50' : 'transparent',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Circle
                        sx={{
                          color: getStatusColor(status),
                          fontSize: 12,
                          mr: 1.5,
                        }}
                      />
                      <Typography variant="body2">{t(`agentStatus.${status}`)}</Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      color={count > 0 ? 'primary.main' : 'text.secondary'}
                    >
                      {count}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" fontWeight="medium">
                  {t('dashboard.agentsStatus.totalAgents')}
                </Typography>
                <Typography variant="h6" color="primary.main">
                  {total}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{t('dashboard.agentsStatus.agentsList')}</Typography>
              <Typography variant="caption" color="textSecondary">
                {t('dashboard.agentsStatus.updatedAgo', { time: '30 seg' })}
              </Typography>
            </Box>

            <Box sx={{ maxHeight: 350, overflowY: 'auto' }}>
              {agentsList.length > 0 ? (
                agentsList.map((agent, index) => (
                  <Box
                    key={agent.userId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1.5,
                      px: 1,
                      borderBottom: index < agentsList.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      '&:hover': { backgroundColor: 'grey.50' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <Circle
                        sx={{
                          color: getStatusColor(agent.status),
                          fontSize: 14,
                          mr: 2,
                        }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {`${agent.firstName} ${agent.lastName}`.trim()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t(`agentStatus.${agent.status}`)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(agent.lastStatusDate).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                ))
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 4,
                    color: 'text.secondary',
                  }}
                >
                  <Typography variant="body2">{t('dashboard.agentsStatus.noAgentsConnected')}</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
};
